import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// MUI components for UI layout and styling
import { Box, Button, Tooltip } from "@mui/material";

// MUI icons for UI interactions
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";

// Components and functions from project
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonTable from "../../custom-components/CommonTestCasesTable";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  TestCaseFlowDataContext,
  TestCaseLoaderStateContext,
} from "../../main-pages/TestcaseGenerationPage";
import { generateTestTechniques } from "../../../services/webServices";

// Icons from project
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import MessagePopup from "../../popup-components/MessagePopup";

const TestConditionsComponent = () => {
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setTestCaseLoaderState } = useContext(TestCaseLoaderStateContext);
  const {
    testConditions,
    setTestConditions,
    functionalSpecDocument,
    testTechniques,
    setTestTechniques,
  } = useContext(TestCaseFlowDataContext);

  const location = useLocation();
  const navigate = useNavigate();

  // State variables for managing UI interactions
  const [error, setError] = useState(null);
  const [activeThumb, setActiveThumb] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const defaultTestConditionExpanded =
    Object.keys(testConditions || {}).length > 0 &&
    Object.keys(testTechniques || {}).length === 0;

  const handleTestConditionsTableData = (testConditionsData) => {
    if (!testConditionsData || typeof testConditionsData !== "object") {
      console.error("Invalid testConditionsData", testConditionsData);
      return { headers: [], values: [] };
    }

    const headers = ["Requirement", "Test conditions"];
    const values = Object.entries(testConditionsData).map(([key, value]) => [
      key,
      flattenArray(value),
    ]);
    return { headers, values };
  };

  const handleTestConditionsDownloadTableData = (testConditionsData) => {
    if (!testConditionsData || typeof testConditionsData !== "object") {
      console.error("Invalid testConditionsData", testConditionsData);
      return { headers: [], values: [] };
    }

    const headers = ["Requirement", "Test Condition"];
    const values = Object.entries(testConditionsData).map(([key, value]) => [
      key,
      formatNumberedList(value),
    ]);

    return { headers, values };
  };

  const formatNumberedList = (arr) => {
    if (!Array.isArray(arr)) return "";

    return arr.map((item, index) => `${index + 1}. ${item}`).join("\n");
  };

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleDownload = () => {
    setDownloadOpen(true);
  };

  const handleGenerateTestTechniques = async () => {
    let fake;

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 1.5;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 98) progress = 98;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));

      }, 200); 

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };
    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Test Techniques",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      setError(null);
      setTestCaseLoaderState("Test Techniques");
      setTestTechniques([]);

      const testTechniques = await generateTestTechniques({
        functionalSpecDocument: functionalSpecDocument["Functional Requirements"],
        testConditions: testConditions,
      });

      if (!testTechniques?.data || Object.keys(testTechniques.data).length === 0) {
        setPopupMessage("No test techniques received from the server.");
        setPopupOpen(true);
        return;
      }
      await finishTo100AndClose();

      navigate("/dashboard/generate-testcases/output", {
        state: {
          ...location.state,
          testTechniques: testTechniques.data || {},
          testcases: [],
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error generating test techniques:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        "An error occurred while generating test techniques.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setError("Error generating test techniques: " + error.message);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const testConditionsTable = (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          maxHeight: "60vh",
          height: "100%",
        }}
      >
        <CommonTable
          {...handleTestConditionsTableData(testConditions)}
          tableData={testConditions}
          setTableData={setTestConditions}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: 7,
          marginBottom: 2,
        }}
      >
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#FF4842",
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <CommonCopy contentToCopy={testConditions} />
      </Box>
    </Box>
  );

  return (
    <Box>
      <CommonAccordion
        summary={"Test Conditions"}
        details={testConditionsTable}
        type={"output"}
        defaultExpanded={defaultTestConditionExpanded}
        fullScreenMode={true}

        buttons={[
          {
            label: "Download",
            variant: "text",
            onClick: handleDownload,
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
          },
          {
            label: "Find Test Techniques",
            onClick: handleGenerateTestTechniques,
            variant: "contained",
            startIcon: <GenerateButton />,
            sx: { float: "right", textTransform: "none" },
          },
        ]}
      />

      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Test Conditions"}
        serviceResponse={[
          {
            heading: "Test Conditions",
            type: "table",
            data: handleTestConditionsDownloadTableData(testConditions),
          },
        ]}
        wordData={[
          {
            heading: "Test Conditions",
            type: "table",
            data: handleTestConditionsDownloadTableData(testConditions),
          },
        ]}
        documents={["docx", "pdf"] || []}
      />


      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />

    </Box>

  );
};

export default TestConditionsComponent;
